import { memo, useRef } from 'react';
import { Settlement } from 'common/src/utils/types';
import AccumulatedFobVarietyChart from '../../../components/settlements/chart-fob-accumulatedByVariety';
import AccumulatedFobFormatChart from '../../../components/settlements/chart-fob-format-chart';
import ChartFobAccumulated from '../../../components/settlements/chart-fob-accumulated';
import ChartFobAccumulatedByCaliber from '../../../components/settlements/chart-fob-accumulated-by-caliber';
import ChartFobAccumulatedByColor from '../../../components/settlements/chart-fob-accumulated-by-color';
import ChartFobAccumulatedByMarket from '../../../components/settlements/chart-fob-accumulated-by-market';
import ChartFobAccumulatedByLabel from '../../../components/settlements/chart-fob-accumulated-by-label';
import AccumulatedFobFleetChart from '../../../components/settlements/chart-fob-accumulated-by-fleet';
import AccumulatedFobCountryChart from '../../../components/settlements/chart-fob-accumulated-by-country';
import AccumulatedFobBusinessTypeChart from '../../../components/settlements/chart-fob-accumulated-by-business-type';
import InformationTooltip from '../../../../../common/src/components/ui/InformationTooltip';
import { Tabs, Tab } from '@nextui-org/react';
import ExcelExportButton from '../../../components/ui/ExcelExportButton';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import { ChartFilters } from '../../../utils/functions';
import { TableExportRef } from '../../../utils/table-styles';

interface ChartsSectionProps {
  settlements: Settlement[];
  chartViewMode: 'week' | 'day';
  chartOrTable: 'chart' | 'table';
  onChartViewModeChange: (value: 'week' | 'day') => void;
  onChartOrTableChange: (value: 'chart' | 'table') => void;
  boxSize?: number;
  dateType: 'eta' | 'etd' | 'sale_date';
  selectedTab: string;
  filters: ChartFilters;
  valueMode: 'average' | 'total';
  onValueModeChange: (value: 'average' | 'total') => void;
  multiplier: number;
}

const ChartsSection = ({
  settlements,
  chartViewMode,
  chartOrTable,
  onChartViewModeChange,
  onChartOrTableChange,
  boxSize,
  dateType,
  selectedTab,
  filters,
  valueMode,
  onValueModeChange,
  multiplier,
}: ChartsSectionProps) => {
  const { t } = useTranslation('settlements');
  const chartRef = useRef<TableExportRef>(null);

  const renderChart = () => {
    switch (selectedTab) {
      case 'variety':
        return (
          <AccumulatedFobVarietyChart
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'format':
        return (
          <AccumulatedFobFormatChart
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'client':
        return (
          <ChartFobAccumulated
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'caliber':
        return (
          <ChartFobAccumulatedByCaliber
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'color':
        return (
          <ChartFobAccumulatedByColor
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'market':
        return (
          <ChartFobAccumulatedByMarket
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'label':
        return (
          <ChartFobAccumulatedByLabel
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'fleet':
        return (
          <AccumulatedFobFleetChart
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'country':
        return (
          <AccumulatedFobCountryChart
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      case 'business_type':
        return (
          <AccumulatedFobBusinessTypeChart
            settlements={settlements}
            chartViewMode={chartViewMode}
            chartOrTable={chartOrTable}
            dateType={dateType}
            filters={filters}
            valueMode={valueMode}
            multiplier={multiplier}
            ref={chartRef}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className='w-full'>
      <div className='flex items-center gap-2 mb-2'>
        <h2 className='text-xl font-bold'>{t('Evolutivo')}</h2>
        {/* <InformationTooltip>
          <div className='max-w-xs p-2'>
            <p className='font-medium mb-2'>{t('Cálculo del FOB Acumulado:')}</p>
            <ol className='list-decimal pl-4 space-y-1 text-sm'>
              <li>
                {t('Se obtiene el FOB total y los kilos de cada detalle de liquidación')}
              </li>
              <li>
                {t('Se calcula el FOB promedio por kilo (FOB total ÷ kilos totales)')}
              </li>
              <li>
                {t('Los datos se agrupan según la vista seleccionada (variedad, formato, etc.)')}
              </li>
              <li>
                {t('Se muestra el promedio FOB por período de tiempo:')}
                <ul className='list-disc pl-4 mt-1'>
                  <li>{t('Por día: promedio diario del FOB/kg')}</li>
                  <li>
                    {t('Por semana: promedio semanal del FOB/kg (comenzando lunes). Las semanas se pueden agrupar por ETD, ETA y Fecha Venta (en filtros)')}
                  </li>
                </ul>
              </li>
              <li>
                {t('Los valores pueden mostrarse por:')}
                <ul className='list-disc pl-4 mt-1'>
                  <li>{t('Kilo: FOB/kg directo')}</li>
                  <li>{t('Caja equivalente: FOB/kg × 5 (caja estándar de 5kg)')}</li>
                </ul>
              </li>
            </ol>
            <div className='mt-4 pt-2 border-t border-divider'>
              <p className='font-semibold text-sm'>
                {t('Conclusión: Estos gráficos muestran cómo evolucionan los precios FOB en el tiempo, permitiendo comparar el rendimiento por variedad, formato, cliente y otros criterios.')}
              </p>
            </div>
          </div>
        </InformationTooltip> */}
        <div className='flex-1' />
        <Tabs
          aria-label={t('Agrupación')}
          selectedKey={chartViewMode}
          onSelectionChange={(key) => onChartViewModeChange(key as 'week' | 'day')}
          size='sm'
          variant='bordered'
        >
          <Tab key='day' title={t('Día')} />
          <Tab key='week' title={t('Semana')} />
        </Tabs>
        <Tabs
          aria-label={t('Vista')}
          selectedKey={chartOrTable}
          onSelectionChange={(key) => onChartOrTableChange(key as 'chart' | 'table')}
          size='sm'
          variant='bordered'
        >
          <Tab key='table' title={t('Tabla')} />
          <Tab key='chart' title={t('Gráfico')} />
        </Tabs>
        <Tabs
          aria-label={t('Valor')}
          selectedKey={valueMode}
          onSelectionChange={(key) => onValueModeChange(key as 'average' | 'total')}
          size='sm'
          variant='bordered'
        >
          <Tab key='total' title={t('Total')} />
          <Tab key='average' title={t('Promedio')} />
        </Tabs>
        <ExcelExportButton onClick={() => chartRef.current?.exportToExcel()} />
      </div>
      {renderChart()}
    </div>
  );
};

export default memo(ChartsSection);
